import React, { useCallback, useEffect, useRef, useState } from 'react'
import { Box, Button, Typography } from '@mui/material'
import MusicNoteIcon from '@mui/icons-material/MusicNote'

import lang from '@/lang'
import Loader from '@/modules/base/components/loader/loader'
import FileService from '@/modules/file/fileService'
import DocumentDrawerMediaControls from './document-drawer-media-controls'

/**
 * FLAG:useDocuAi — video/audio preview for the document drawer.
 *
 * Ported from the DMS admindashboard's `MediaViewer`
 * (`dms-s3/frontend/admindashboard/src/components/filelist/subcomponents/MediaViewer.tsx`)
 * plus the media state/handlers that live in its `dialogVersionComponent`, so both
 * surfaces behave the same: custom transport bar, `controls={false}` on the element,
 * ±10 s skip, fullscreen on the container, and an audio file rendered as a note card
 * driving a hidden <audio>.
 *
 * Three deliberate departures from the original, each fixing something that would be a
 * real defect in this host:
 *
 *  1. **Element ref instead of `document.getElementById('video-player')`.** The drawer can
 *     mount several previews at once (document-carousel-wrapper renders slides), so a fixed
 *     DOM id is not unique here and the controls would drive whichever element happened to
 *     be first in the document.
 *  2. **State is owned here**, not prop-drilled from the drawer. The admindashboard hoists
 *     six pieces of media state into a 1000-line dialog; the drawer only needs to say
 *     "render this file".
 *  3. **`fullscreenchange` is observed** rather than assumed. The original flips its own
 *     boolean, so leaving fullscreen with ESC (which does not go through the button) left
 *     the icon stuck on "exit fullscreen".
 *
 * The source is a blob object URL, not the base64 `data:` URL `streamDmsFile` returns —
 * see `fileService.streamDmsFileAsObjectUrl` for why seeking depends on it.
 */

// How long a document may go without a `minioId` before we call it broken. Generous on purpose:
// it is only ever reached by a document that will never get one, and the cost of being early is
// an error message over a file that was about to load.
const MISSING_MINIO_ID_GRACE_MS = 15000

interface DocumentDrawerPreviewMediaProps {
  document: {
    minioId?: string
    mimetype?: string
    name?: string
    decodedName?: string
  }
}

const DocumentDrawerPreviewMedia: React.FC<DocumentDrawerPreviewMediaProps> = ({ document: doc }) => {
  const l = lang.documents.mediaPlayer
  const mimetype = (doc?.mimetype || '').toLowerCase()
  const isAudio = mimetype.startsWith('audio/')
  const fileName = doc?.decodedName ?? doc?.name

  const mediaRef = useRef<HTMLMediaElement | null>(null)
  const containerRef = useRef<HTMLDivElement | null>(null)
  // Web Audio graph, built LAZILY and only to amplify past 100%. `media.volume` is capped at
  // 1.0 by spec, so a quiet source (measured: element at volume 1, unmuted, still too quiet)
  // cannot be made louder any other way. Built on demand rather than always, because
  // createMediaElementSource permanently reroutes the element's audio through the graph — if
  // anything about it misbehaves the sound is gone entirely. Staying off the graph for the
  // 0–100% range keeps the normal path exactly as the browser's own.
  const audioCtxRef = useRef<AudioContext | null>(null)
  const gainRef = useRef<GainNode | null>(null)

  const [src, setSrc] = useState<string | null>(null)
  // Two genuinely different failures, and telling the user "could not be loaded" for the
  // second one would send them hunting a network problem that isn't there. The DMS accepts
  // AVI (`video/x-msvideo`) and `video/mpeg`, which no mainstream browser decodes — those
  // arrive fine and then the ELEMENT rejects them.
  const [failure, setFailure] = useState<null | 'fetch' | 'codec'>(null)
  const [isPlaying, setIsPlaying] = useState(false)
  const [volume, setVolume] = useState(1)
  const [isMuted, setIsMuted] = useState(false)
  const [isFullscreen, setIsFullscreen] = useState(false)
  const [currentTime, setCurrentTime] = useState(0)
  const [duration, setDuration] = useState(0)

  // Fetch as a blob URL and revoke it again on unmount / document change. Without the
  // revoke the whole media file stays resident for as long as the tab lives.
  useEffect(() => {
    let objectUrl: string | null = null
    let cancelled = false

    setSrc(null)
    setFailure(null)
    setIsPlaying(false)
    setCurrentTime(0)
    setDuration(0)

    // No minioId (yet) is a LOADING state, not a failure — see the grace-period effect below.
    if (doc?.minioId) {
      FileService.streamDmsFileAsObjectUrl(doc.minioId, doc.mimetype)
        .then((url: string) => {
          if (cancelled) {
            URL.revokeObjectURL(url)
            return
          }
          objectUrl = url
          setSrc(url)
        })
        .catch(() => !cancelled && setFailure('fetch'))
    }

    return () => {
      cancelled = true
      if (objectUrl) URL.revokeObjectURL(objectUrl)
    }
  }, [doc?.minioId, doc?.mimetype])

  // The drawer hands us its document object, and that object is REPLACED wholesale several times
  // while a fresh upload is being analysed — every FILE:ANALYSED / image-stats / access event
  // dispatches a re-fetch. An intermediate payload can be missing `minioId`, and treating that as
  // "could not be loaded" would flash the error state at the user mid-upload. It is a loading
  // state: a useDocuAi tenant is DMS-backed by definition, so the id does arrive.
  // The grace period exists only so that a genuinely broken document (no id, ever) still ends in
  // the honest error state instead of spinning forever.
  useEffect(() => {
    if (doc?.minioId) return undefined
    const timer = setTimeout(() => setFailure('fetch'), MISSING_MINIO_ID_GRACE_MS)
    return () => clearTimeout(timer)
  }, [doc?.minioId])

  // Release the AudioContext with the component; browsers cap how many can exist at once, and
  // reopening the drawer would otherwise leak one per media document viewed.
  useEffect(() => () => {
    audioCtxRef.current?.close?.().catch(() => {})
    audioCtxRef.current = null
    gainRef.current = null
  }, [])

  // The browser owns fullscreen state — ESC and the F11 path never touch our button.
  useEffect(() => {
    const onChange = () => setIsFullscreen(!!globalThis.document.fullscreenElement)
    globalThis.document.addEventListener('fullscreenchange', onChange)
    return () => globalThis.document.removeEventListener('fullscreenchange', onChange)
  }, [])

  const handlePlayPause = useCallback(() => {
    const media = mediaRef.current
    if (!media) return
    if (media.paused) media.play()
    else media.pause()
    // isPlaying follows the element's own play/pause events, so a play() rejected by an
    // autoplay policy cannot leave the button lying about the state.
  }, [])

  /** Build (once) the gain graph used for >100%. Returns null when Web Audio is unavailable. */
  const ensureGain = useCallback((): GainNode | null => {
    if (gainRef.current) return gainRef.current
    const media = mediaRef.current
    if (!media) return null
    try {
      const Ctx: typeof AudioContext | undefined
        = globalThis.AudioContext
        || (globalThis as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext
      if (!Ctx) return null
      const ctx = new Ctx()
      const gain = ctx.createGain()
      ctx.createMediaElementSource(media).connect(gain).connect(ctx.destination)
      audioCtxRef.current = ctx
      gainRef.current = gain
      return gain
    } catch {
      // A second createMediaElementSource on the same element throws; never let that mute
      // the player — fall back to the plain element volume.
      return null
    }
  }, [])

  const applyVolume = useCallback((next: number) => {
    const media = mediaRef.current
    if (!media) return
    if (next <= 1) {
      // Normal range: the browser's own volume, graph (if one was ever built) held neutral.
      if (gainRef.current) gainRef.current.gain.value = 1
      media.volume = next
    } else {
      const gain = ensureGain()
      if (gain) {
        media.volume = 1
        gain.gain.value = next
        // A context created outside a gesture starts suspended, which would be silence.
        audioCtxRef.current?.resume?.()
      } else {
        media.volume = 1 // no Web Audio ⇒ 100% is the ceiling
      }
    }
    media.muted = next === 0
  }, [ensureGain])

  const handleVolumeChange = useCallback((newVolume: number) => {
    applyVolume(newVolume)
    setVolume(newVolume)
    setIsMuted(newVolume === 0)
  }, [applyVolume])

  const handleMute = useCallback(() => {
    const media = mediaRef.current
    if (!media) return
    const nextMuted = !media.muted
    media.muted = nextMuted
    if (!nextMuted) {
      // Restore whatever level was set, boost included.
      const restored = volume > 0 ? volume : 1
      applyVolume(restored)
      media.muted = false
      setVolume(restored)
    }
    setIsMuted(nextMuted)
  }, [volume, applyVolume])

  const handleSeek = useCallback((time: number) => {
    const media = mediaRef.current
    if (!media || !Number.isFinite(time)) return
    media.currentTime = time
    setCurrentTime(time)
  }, [])

  const handleSkip = useCallback((seconds: number) => {
    const media = mediaRef.current
    if (!media) return
    const next = Math.max(0, Math.min(media.currentTime + seconds, media.duration || 0))
    media.currentTime = next
    setCurrentTime(next)
  }, [])

  const handleFullscreen = useCallback(() => {
    const container = containerRef.current
    if (!container) return
    if (globalThis.document.fullscreenElement) globalThis.document.exitFullscreen?.()
    else container.requestFullscreen?.()
  }, [])

  const mediaEvents = {
    onLoadedMetadata: (e: React.SyntheticEvent<HTMLMediaElement>) =>
      setDuration((e.target as HTMLMediaElement).duration),
    onTimeUpdate: (e: React.SyntheticEvent<HTMLMediaElement>) =>
      setCurrentTime((e.target as HTMLMediaElement).currentTime),
    onPlay: () => setIsPlaying(true),
    onPause: () => setIsPlaying(false),
    onEnded: () => setIsPlaying(false),
    onError: () => setFailure('codec'),
  }

  if (failure) {
    return (
      <Box sx={{ p: 4, textAlign: 'center', maxWidth: 560, mx: 'auto' }}>
        <Typography variant='body2' color='text.secondary'>
          {failure === 'codec' ? l.notPlayable : l.loadFailed}
        </Typography>
        {/* Only on `codec`: the bytes DID arrive (that is how we learned the browser cannot
            decode them), so the already-fetched blob can be handed straight to the user for a
            local player — no second download from the DMS. On `fetch` there is nothing to give. */}
        {failure === 'codec' && src && (
          <Button
            variant='outlined'
            size='small'
            sx={{ mt: 2 }}
            component='a'
            href={src}
            download={fileName}
          >
            {l.download}
          </Button>
        )}
      </Box>
    )
  }

  if (!src) {
    return <Loader text={l.loading} />
  }

  const controls = (
    <DocumentDrawerMediaControls
      isPlaying={isPlaying}
      currentTime={currentTime}
      duration={duration}
      volume={volume}
      isMuted={isMuted}
      isFullscreen={isFullscreen}
      onPlayPause={handlePlayPause}
      onSeek={handleSeek}
      onVolumeChange={handleVolumeChange}
      onMute={handleMute}
      onFullscreen={handleFullscreen}
      onSkipBackward={() => handleSkip(-10)}
      onSkipForward={() => handleSkip(10)}
    />
  )

  if (isAudio) {
    return (
      <Box sx={{ width: '100%', p: 2 }} ref={containerRef}>
        <Box sx={{
          border: '1px solid #333',
          boxShadow: '0 4px 8px rgba(0, 0, 0, 0.2)',
          bgcolor: 'white',
          borderRadius: 1,
          p: 4,
          maxWidth: 640,
          mx: 'auto',
          textAlign: 'center',
        }}>
          <MusicNoteIcon sx={{ fontSize: 64, color: 'primary.main', mb: 2 }} />
          <Typography variant='h6' sx={{ mb: 2, wordBreak: 'break-all' }}>{fileName}</Typography>
          {controls}
          <audio ref={mediaRef as React.RefObject<HTMLAudioElement>} src={src}
            style={{ display: 'none' }} {...mediaEvents} />
        </Box>
      </Box>
    )
  }

  return (
    // Fill the pane: a column whose video row takes all remaining height. The previous
    // `maxWidth: 800` (inherited from the admindashboard, where the dialog is a fixed size)
    // capped the picture at 800px inside a ~1120px pane and letterboxed the rest in black.
    // `minHeight: 0` on both the column and the video row is what lets a flex child actually
    // shrink below its content — without it the row keeps its intrinsic height and the video
    // overflows instead of fitting.
    <Box sx={{ width: '100%', height: '100%', p: 2, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
      {controls}
      <Box
        ref={containerRef}
        sx={{
          flex: 1,
          minHeight: 0,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          bgcolor: 'black',
          borderRadius: 1,
          overflow: 'hidden',
        }}
      >
        <video
          ref={mediaRef as React.RefObject<HTMLVideoElement>}
          src={src}
          // object-fit: contain ⇒ as large as the pane allows, aspect ratio kept, no cropping.
          // Also what makes fullscreen fill the screen properly.
          style={{ width: '100%', height: '100%', objectFit: 'contain', display: 'block' }}
          controls={false} // the transport bar above is the control surface
          // Paint the first frame without waiting for a play gesture. There is no `poster` to
          // fall back on, so with the default preload the pane is an empty black rectangle until
          // the user presses play — which reads as "the video did not load". The bytes are
          // already local (src is a blob URL), so this costs nothing extra.
          preload='auto'
          playsInline
          {...mediaEvents}
        />
      </Box>
    </Box>
  )
}

export default DocumentDrawerPreviewMedia
